import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react"

import { ReactComponent as IconFilters } from "@common_assets/svg/filters.svg"
import {
  Box,
  Popover,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material"
import { useMutationTransactions } from "api/reactQuery/mutations/transactions"
import { useQueryExportTeacherTransactions } from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import {
  useInfiniteTeacherTransactions,
  useQueryTeacherTransactionsFilterData,
  useQueryTeacherTransactionsIds,
} from "api/reactQuery/queries/transaction"
import { ITeacherTransactionsFilters } from "api/reactQuery/queries/transaction.types"
import DeleteTransactionsDialog from "components/common/deleteTransactionsDialog"
import ErrorText from "components/common/error/errorText"
import {
  ExportButton,
  ExportConfirmDialog,
  ExportMenu,
  ExportPendingBanner,
} from "components/common/exportMenu"
import LoadingIndicator from "components/common/loadingIndicator"
import BasicTable from "components/common/table/basicTable"
import { PrimaryTile } from "components/common/tile"
import TransactionsSelectionBar from "components/common/transactionsSelectionBar"
import TransactionsTableArea from "components/common/transactionsTableArea"
import SearchbarForm from "components/form/searchbarForm"
import { Dayjs } from "dayjs"
import { useIsBreakpointUp } from "hooks/breakpoint"
import { useTransactionsDeleteFlow } from "hooks/deleteTransactionsTask"
import { useDialog } from "hooks/dialog"
import { useExportGuard, useExportMenu, useExportTasks } from "hooks/exportReports"
import { useCustomPayment } from "hooks/payment"
import { usePopover } from "hooks/popover"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import { useTransactionSelection } from "hooks/transactionSelection"
import mixpanel from "mixpanel-browser"
import { useTranslation } from "react-i18next"
import { useInView } from "react-intersection-observer"
import { CSSTransition, TransitionGroup } from "react-transition-group"
import { selectUser } from "store/auth/auth.selectors"
import { ExportIconWrapper } from "styles/common/box"
import { visuallyHiddenSx } from "styles/common/typography"
import { IClassWithGroups } from "ts/interfaces/Transaction"
import { getErrorMessageFromTab } from "utils/api"
import { schoolPlanToExportData } from "utils/export"
import { isSameQueryParams } from "utils/reactQuery"

import { SenderType } from "../../../ts/types/SenderType"
import DialogUpgradeAccount from "../../common/dialogUpgradeAccount/DialogUpgradeAccount"
import {
  buildFilterChips,
  draftToFilters,
  emptyFiltersDraft,
  filtersToDraft,
  hasPopoverFilters,
  IFiltersDraft,
  isStudentStorePurchase,
  removeFilterChip,
} from "./AllTransactionsTeacher.utility"
import BodyAllTransactions from "./bodyAllTransactions"
import FilterOptions from "./filterOptions"
import HeadAllTransactions from "./headAllTransactions"
import PopoverContent from "./popoverContent"
import {
  pruneClassTreeSelection,
  toggleClass,
  toggleGroup,
  typeTransactions,
} from "./popoverContent/PopoverContent.utility"

export type TypeTransactions = typeof typeTransactions[number]

export interface ClassnameProps {
  kind: "class" | "group" | "sender"
  name: string
  id: number
}

const AllTransactionsTeacher = () => {
  const theme = useTheme()
  const [isExportData, setIsExportData] = useState(false)
  const [draft, setDraft] = useState<IFiltersDraft>(emptyFiltersDraft)
  const [appliedFilters, setAppliedFilters] =
    useState<ITeacherTransactionsFilters>(() =>
      draftToFilters(emptyFiltersDraft, {})
    )
  const isFilterActive = hasPopoverFilters(appliedFilters)
  const { t } = useTranslation()
  const { showSnackbar } = useCustomSnackbar()
  const isMobile = !useIsBreakpointUp("tablet")
  const userInfo = useAppSelector(selectUser)

  const { handleClick, handleClose, popoverAnchorEl } =
    usePopover<HTMLDivElement>()

  const { payIt, isLoadingPayments } = useCustomPayment()

  const { data: filterData, isLoading: isLoadingFilterData } =
    useQueryTeacherTransactionsFilterData({
      options: {
        enabled: !!popoverAnchorEl,
        onSuccess: (response) => {
          setDraft((prev) => ({
            ...prev,
            classSelection: pruneClassTreeSelection(
              prev.classSelection,
              response.data.classes
            ),
          }))
        },
      },
    })
  const filterClasses = filterData?.data.classes ?? []

  const { ref, inView } = useInView()

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteTeacherTransactions({
    filters: appliedFilters,
    limit: 20,
    options: {
      onError: (err) => {
        showSnackbar({
          title: getErrorMessageFromTab(err),
          variant: "error",
        })
      },
    },
  })
  const transactions = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  )

  const selectableIds = useMemo(
    () =>
      transactions
        .filter((transaction) => !isStudentStorePurchase(transaction))
        .map(({ id }) => id),
    [transactions]
  )

  const selection = useTransactionSelection({
    selectableIds,
    useMatchingIds: (enabled) =>
      useQueryTeacherTransactionsIds({
        filters: appliedFilters,
        options: { enabled },
      }),
  })

  const commitFilters = (filters: ITeacherTransactionsFilters) => {
    if (isSameQueryParams(filters, appliedFilters)) return

    setAppliedFilters(filters)
    selection.clear()
  }

  const exportQueryParams = useMemo(
    () => ({ ...appliedFilters }),
    [appliedFilters]
  )

  const { isFetching, refetch: refetchExportData } =
    useQueryExportTeacherTransactions({
      ...exportQueryParams,
      options: {
        enabled: isExportData,
        onSuccess: (response) => {
          setIsExportData(false)
          handleExportSuccess(response)
          onExportTriggered()
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const deleteFlow = useTransactionsDeleteFlow({
    view: "teacher",
    scopeId: userInfo?.user.id,
    onDeleted: selection.clear,
  })
  const { activeTask } = deleteFlow

  const { mutate, isLoading: isDeleting } = useMutationTransactions({
    options: deleteFlow.mutationOptions,
  })

  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !activeTask) {
      fetchNextPage()
    }
  }, [inView, hasNextPage, isFetchingNextPage, activeTask])

  const handleSearchSubmit = (
    key: "title_filter" | "student_filter",
    search: string
  ) => {
    const value = search.trim()
    if (value.length === 1) return

    commitFilters({ ...appliedFilters, [key]: value || undefined })
  }

  const handleCheckboxTransactions = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const checkedName = event.target.name.toLowerCase()
    const isChecked = event.target.checked

    setDraft((prev) => ({
      ...prev,
      typeTransactions: isChecked
        ? [...prev.typeTransactions, checkedName]
        : prev.typeTransactions.filter((type) => type !== checkedName),
    }))
  }

  const handleToggleClass = (classItem: IClassWithGroups) => {
    setDraft((prev) => ({
      ...prev,
      classSelection: toggleClass(prev.classSelection, classItem),
    }))
  }

  const handleToggleGroup = (classItem: IClassWithGroups, groupId: number) => {
    setDraft((prev) => ({
      ...prev,
      classSelection: toggleGroup(prev.classSelection, classItem, groupId),
    }))
  }

  const handleDateFrom = (date: Dayjs | null) => {
    setDraft((prev) => ({ ...prev, dateFrom: date }))
  }

  const handleDateTo = (date: Dayjs | null) => {
    setDraft((prev) => ({ ...prev, dateTo: date }))
  }

  const getSenderType = (type: SenderType) => {
    setDraft((prev) => ({ ...prev, senderType: type }))
  }

  const resetAllOptions = () => {
    setDraft(emptyFiltersDraft)
  }

  const filterChips = buildFilterChips(
    appliedFilters,
    filterClasses,
    (sender) => t("allTransactions.senderChip", { sender })
  )

  const {
    isOpen: isOpenExport,
    handleOpen: handleOpenExport,
    handleClose: handleCloseExport,
  } = useDialog()

  const {
    successTasks,
    pendingTasks,
    refreshTasks,
    setExportReportIdToDownload,
    handleExportSuccess,
  } = useExportTasks({
    exportType: ExportType.TEACHER_TRANSACTIONS,
  })

  const {
    exportMenuAnchorEl,
    isExportMenuOpen,
    handleExportClick,
    handleExportMenuClose,
    handleGenerateNewFile: handleGenerateFile,
  } = useExportMenu(isFetching, refreshTasks)

  const {
    isGenerateDisabled,
    isConfirmDialogOpen,
    handleCloseConfirmDialog,
    handleConfirm: handleConfirmExport,
    handleGenerate: guardGenerate,
    onExportTriggered,
  } = useExportGuard({
    hasPendingTasks: pendingTasks.length > 0,
    queryParams: exportQueryParams,
  })

  const triggerExport = useCallback(() => {
    schoolPlanToExportData({
      user: userInfo,
      upgrade: handleOpenExport,
      exportData: refetchExportData,
    })
  }, [userInfo, handleOpenExport, refetchExportData])

  const handleGenerateWithConfirmation = useCallback(() => {
    handleGenerateFile(() => guardGenerate(triggerExport))
  }, [handleGenerateFile, guardGenerate, triggerExport])

  return (
    <PrimaryTile sx={{ p: 2 }}>
      <Typography variant="h1" sx={visuallyHiddenSx}>
        {t("layout.allTransactions")}
      </Typography>
      <Box
        sx={{ display: "flex", columnGap: "10px", mb: 3 }}
        alignItems="center"
      >
        <Stack
          width="100%"
          columnGap={1}
          rowGap={2}
          direction={isMobile ? "column" : "row"}
        >
          <SearchbarForm
            placeholder={`${t(
              "allTransactions.searchForStudentOrTransactionTitle",
              { things: "transaction title" }
            )}...`}
            onSubmit={(data) => handleSearchSubmit("title_filter", data.search)}
          />
          <SearchbarForm
            placeholder={`${t(
              "allTransactions.searchForStudentOrTransactionTitle",
              { things: "student" }
            )}...`}
            onSubmit={(data) =>
              handleSearchSubmit("student_filter", data.search)
            }
          />
        </Stack>
        <Stack rowGap={2} direction={isMobile ? "column" : "row"} columnGap={1}>
          <Tooltip
            title={t("allTransactions.options")}
            arrow={true}
            placement="top"
          >
            <ExportIconWrapper
              role="button"
              tabIndex={0}
              aria-label={t("allTransactions.options")}
              onClick={handleClick}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault()
                  handleClick(
                    event as unknown as React.MouseEvent<HTMLDivElement>
                  )
                }
              }}
              sx={{
                minWidth: "48px",
                height: "48px",
                m: "auto",
                color: theme.palette.primary.main,
                ...(isFilterActive && {
                  color: theme.palette.mockup.neutral100,
                  background: theme.palette.primary.main,
                  "&:hover": {
                    background: theme.palette.mockup.primary20,
                  },
                }),
              }}
            >
              <IconFilters style={{ margin: "auto" }} />
            </ExportIconWrapper>
          </Tooltip>
          <DialogUpgradeAccount
            open={isOpenExport}
            onClose={() => {
              handleCloseExport()
              mixpanel.track("reports_export", {
                method: "teacher_all_transactions",
                action: "cancel",
              })
            }}
            onActionButtonClick={() => {
              payIt(null)
              mixpanel.track("reports_export", {
                method: "teacher_all_transactions",
                action: "submit",
              })
            }}
            isLoading={isLoadingPayments}
            isExport
          />
          <ExportButton isFetching={isFetching} onClick={handleExportClick} />
          <ExportMenu
            anchorEl={exportMenuAnchorEl}
            open={isExportMenuOpen}
            onClose={handleExportMenuClose}
            onGenerateNewFile={handleGenerateWithConfirmation}
            onFileClick={setExportReportIdToDownload}
            successTasks={successTasks}
            isGenerateDisabled={isGenerateDisabled}
            generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
          />
        </Stack>
        <Popover
          open={!!popoverAnchorEl}
          anchorEl={popoverAnchorEl}
          marginThreshold={60}
          onClose={handleClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <PopoverContent
            {...{
              typeTransactionsArray: draft.typeTransactions,
              selectedDateFrom: draft.dateFrom,
              selectedDateTo: draft.dateTo,
              handleCheckboxTransactions,
              handleToggleClass,
              handleToggleGroup,
              handleDateFrom,
              handleDateTo,
              classes: filterClasses,
              isLoadingClasses: isLoadingFilterData,
              classSelection: draft.classSelection,
              isLoading,
              senderType: draft.senderType,
              getSenderType,
            }}
            onApply={(errorFrom, errorTo) => {
              if (errorFrom || errorTo) return

              commitFilters(draftToFilters(draft, appliedFilters))
              handleClose()
            }}
            onReset={resetAllOptions}
          />
        </Popover>
      </Box>
      <FilterOptions
        filterName={filterChips}
        onClick={(chip) => {
          setDraft((prev) => removeFilterChip(prev, chip))
          commitFilters(
            draftToFilters(
              removeFilterChip(filtersToDraft(appliedFilters), chip),
              appliedFilters
            )
          )
        }}
        onClickReset={() => {
          resetAllOptions()
          commitFilters(draftToFilters(emptyFiltersDraft, appliedFilters))
        }}
      />
      <TransactionsTableArea deletingCount={activeTask?.count ?? null}>
        <BasicTable
          tableProps={{ sx: { height: "1px", tableLayout: "fixed", minWidth: 700 } }}
          topSlot={
            <TransactionsSelectionBar
              {...selection.bar}
              onDeleteSelected={() => deleteFlow.openBatch(selection.selection)}
            />
          }
          head={
            <HeadAllTransactions
              onChange={selection.header.onToggle}
              isChecked={selection.header.isChecked}
              isIndeterminate={selection.header.isIndeterminate}
              isDisabled={selection.header.isDisabled}
            />
          }
          body={
            <TransitionGroup component={null}>
              {transactions.map((transaction) => (
                <CSSTransition
                  key={transaction.id}
                  timeout={500}
                  classNames="animation-fade"
                >
                  <BodyAllTransactions
                    key={transaction.id}
                    data={transaction}
                    onClick={() =>
                      deleteFlow.openSingle(transaction.id, transaction.title)
                    }
                    getChecked={selection.isRowChecked(transaction.id)}
                    onChange={() => selection.toggleRow(transaction.id)}
                  />
                </CSSTransition>
              ))}
            </TransitionGroup>
          }
          bottomSlot={
            <Box textAlign="center" py={2} ref={ref}>
              {isError && <ErrorText />}
              {(isLoading || isFetchingNextPage) && <LoadingIndicator />}
              {!isLoading && !transactions.length && !isError && (
                <Typography variant="body1" color="mockup.neutral10">
                  {t("allTransactions.noResults")}
                </Typography>
              )}
            </Box>
          }
        />
      </TransactionsTableArea>
      <DeleteTransactionsDialog
        {...deleteFlow.dialog}
        isPending={isDeleting}
        onConfirm={() =>
          deleteFlow.request &&
          mutate({ transaction_ids: deleteFlow.request.ids })
        }
      />
      <ExportConfirmDialog
        open={isConfirmDialogOpen}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleConfirmExport}
      />
      <ExportPendingBanner visible={pendingTasks.length > 0} />
    </PrimaryTile>
  )
}

export default AllTransactionsTeacher
